import router from '@/router'
import httpClient from './httpClient'
import type { IUserRole } from '@/type'

// User Type
export enum UserType {
  ADMIN = 'admin',
  ORGANIZATION_USER = 'customer_user',
  ASSEMBLERHUB_USER = 'supplier_user',
}

export interface LoginPayload {
  email: string
  password: string
  user_type: UserType.ORGANIZATION_USER
}

interface Salutation {
  id: number
  title: string
  formal_salutation: string
}

interface MainContactPerson {
  id: number
  customer_id: number
  salutation_id: number
  first_name: string
  last_name: string
  salutation_letter: string
  position: string
  phone: string
  email: string
  is_main_contact_person: boolean
  updated_at: string
}

interface IndividualField {
  id: number
  value: boolean | string
}

export interface Customer {
  id: number
  name: string
  number: number
  salutation_id: number
  salutation: Salutation
  street: string
  postal_code: string
  city: string
  country: string
  phone: string
  email: string
  website: string
  language_id: number
  currency_id: number
  tax_id: string
  intrastat_not_include: boolean
  tax_type: number
  price_group: number
  is_deleted: boolean
  main_contact_person_id: number
  main_contact_person: MainContactPerson
  individual_fields: IndividualField[]
  created_at: string
  updated_at: string
}

interface Organization {
  id: number
  name: string
  created_at: string
  updated_at: string
}
export interface AuthUser {
  id: number
  first_name: string
  middle_name: string | null
  last_name: string
  email: string
  user_type: string
  customer_id?: number
  created_at: string
  updated_at: string
  roles: IUserRole
  /** The customer the session is currently acting as. */
  customer: Customer
  /**
   * Every customer this account may act as. Only sent for accounts assigned to
   * more than one customer, so treat an absent list as "just `customer`".
   * All entries belong to the same organization — that is enforced server-side.
   */
  customers?: Customer[]
  organization: Organization
  [key: string]: unknown
}

export interface LoginResponse {
  token: string
  token_type: string
  token_expired_at: string
  user: AuthUser
  [key: string]: unknown
}

/**
 * What `/auth/check_reset_token` says about a password link:
 * `valid` — the link works; `expired` — older than 48 hours;
 * `invalid` — already used, replaced by a newer mail, or unknown user;
 * `inactive` — the account is not active.
 */
export type ResetTokenStatus = 'valid' | 'expired' | 'invalid' | 'inactive'

const RESET_TOKEN_STATUSES: ResetTokenStatus[] = ['valid', 'expired', 'invalid', 'inactive']

export interface UpdateProfilePayload {
  first_name?: string
  middle_name?: string | null
  last_name?: string
  email?: string
  customer_id?: number
}

const AuthService = {
  async login(payload: LoginPayload): Promise<LoginResponse> {
    const { data } = await httpClient.post<LoginResponse>('/auth/login', payload)
    return data
  },

  async getLoginUser(): Promise<AuthUser> {
    const { data } = await httpClient.get('/auth/user')
    return data
  },

  async logout(): Promise<void> {
    console.log('logout')
    const token = localStorage.getItem('authToken')

    await httpClient.delete('/auth/logout', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    console.log('logout success')
    localStorage.removeItem('authToken')
    localStorage.removeItem('authTokenType')
    localStorage.removeItem('authTokenExpiredAt')
    localStorage.removeItem('authUser')
    router.push('/login')
  },

  async getCurrentUser(): Promise<{ data: AuthUser }> {
    const response = await httpClient.get<AuthUser>('/auth/user')
    return response
  },

  async updateProfile(payload: UpdateProfilePayload): Promise<{ data: AuthUser }> {
    const response = await httpClient.put<AuthUser>('/auth/user', payload)
    return response
  },

  /**
   * Point the current session at another of the user's assigned customers.
   * The bearer token stays valid; the response carries the refreshed user with
   * the new active `customer`. The backend rejects any customer the account is
   * not assigned to, so the returned user is the source of truth — never the id
   * we sent.
   */
  async switchCustomer(customerId: number): Promise<{ data: AuthUser }> {
    const response = await httpClient.post<AuthUser>('/auth/switch_customer', {
      customer_id: customerId,
    })
    return response
  },

  async sendForgotPasswordLink(payload: { email: string }): Promise<{ message: string }> {
    const response = await httpClient.post('/auth/password_recovery', {
      ...payload,
      user_type: 'customer_user',
    })
    return response.data
  },

  /**
   * Check a password link before the form is shown. An unknown answer counts as
   * `valid`, so the form stays usable and the save reports the real reason.
   * The page shows its own state, so the global error toast is skipped.
   */
  async checkResetToken(payload: { id: number; token: string }): Promise<ResetTokenStatus> {
    const response = await httpClient.post('/auth/check_reset_token', payload, {
      skipErrorToast: true,
    })
    const status = (response.data?.data ?? response.data)?.status
    return RESET_TOKEN_STATUSES.includes(status) ? status : 'valid'
  },

  /** The reset page shows its own error, so the global error toast is skipped. */
  async setPassword(payload: {
    id: number
    token: string
    password: string
  }): Promise<{ message: string }> {
    const response = await httpClient.post('/auth/set_password', payload, {
      skipErrorToast: true,
    })
    return response.data
  },
}

export default AuthService
